"use client";

import { CaretDown } from "@phosphor-icons/react/dist/ssr";
import { Accordion as RadixAccordion } from "radix-ui";
import type { ComponentPropsWithoutRef, ComponentRef } from "react";

import { cn } from "@/services/styles/cn";

const { Content, Header, Item, Root, Trigger } = RadixAccordion;

function Accordion({ children, className, ...props }: ComponentPropsWithoutRef<typeof Root>) {
  return (
    <Root className={cn("w-full", className)} {...props}>
      {children}
    </Root>
  );
}

function AccordionItem({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Item> & { ref?: React.RefObject<ComponentRef<typeof Item> | null> }) {
  return <Item ref={ref} className={cn("border-foreground border-b", className)} {...props} />;
}
AccordionItem.displayName = "AccordionItem";

function AccordionTrigger({
  ref,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof Trigger> & { ref?: React.RefObject<ComponentRef<typeof Trigger> | null> }) {
  return (
    <Header className="flex">
      <Trigger
        ref={ref}
        className={cn(
          "flex flex-1 cursor-pointer items-center justify-between gap-2 py-5 text-left font-serif text-3xl text-foreground transition-all lg:text-4xl [&[data-state=open]>svg]:rotate-180",
          className,
        )}
        {...props}
      >
        <span>{children}</span>
        <CaretDown className="shrink-0 transition-transform duration-200" />
      </Trigger>
    </Header>
  );
}
AccordionTrigger.displayName = Trigger.displayName;

function AccordionContent({
  ref,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof Content> & { ref?: React.RefObject<ComponentRef<typeof Content> | null> }) {
  return (
    <Content
      ref={ref}
      className="overflow-hidden text-base transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn("pt-0 pb-4", className)}>{children}</div>
    </Content>
  );
}

AccordionContent.displayName = Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
